import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";

const PHASES = [
  { key: "inhale", label: "Breathe in", duration: 4 },
  { key: "hold", label: "Hold", duration: 2 },
  { key: "exhale", label: "Breathe out", duration: 6 },
];

export default function BreathingExercise() {
  const [running, setRunning] = useState(false);
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [cycles, setCycles] = useState(0);
  const timer = useRef(null);

  const phase = PHASES[phaseIdx];

  useEffect(() => {
    if (!running) return;
    timer.current = setTimeout(() => {
      setPhaseIdx((prev) => {
        const next = (prev + 1) % PHASES.length;
        if (next === 0) setCycles((c) => c + 1);
        return next;
      });
    }, phase.duration * 1000);
    return () => clearTimeout(timer.current);
  }, [running, phaseIdx, phase.duration]);

  const scale = phase.key === "inhale" ? 1.35 : phase.key === "exhale" ? 0.75 : 1.35;

  const toggle = () => {
    if (running) {
      setRunning(false);
      setPhaseIdx(0);
    } else {
      setPhaseIdx(0);
      setCycles(0);
      setRunning(true);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center py-8" data-testid="breathing-exercise">
      <div className="relative flex items-center justify-center w-64 h-64 mb-6">
        <motion.div
          className="absolute rounded-full bg-sky/20"
          animate={{ scale: running ? scale : 1 }}
          transition={{ duration: running ? phase.duration : 0.6, ease: "easeInOut" }}
          style={{ width: 180, height: 180 }}
        />
        <motion.div
          className="absolute rounded-full bg-sky/40"
          animate={{ scale: running ? scale : 1 }}
          transition={{ duration: running ? phase.duration : 0.6, ease: "easeInOut" }}
          style={{ width: 130, height: 130 }}
        />
        <motion.div
          className="rounded-full bg-sky flex items-center justify-center text-sand"
          animate={{ scale: running ? scale : 1 }}
          transition={{ duration: running ? phase.duration : 0.6, ease: "easeInOut" }}
          style={{ width: 90, height: 90 }}
        >
          <span className="font-serif text-lg text-center leading-tight px-2">
            {running ? phase.label : "Ready"}
          </span>
        </motion.div>
      </div>

      <button
        data-testid="breathing-toggle"
        onClick={toggle}
        className="rounded-full bg-sky px-8 py-3 text-sand font-medium transition-colors duration-300 hover:bg-sky/85"
      >
        {running ? "Stop" : "Begin breathing"}
      </button>
      {running && <p className="mt-4 text-sm text-stone" data-testid="breathing-cycles">Cycles completed: {cycles}</p>}
      {!running && <p className="mt-4 text-sm text-stone">4s in · 2s hold · 6s out</p>}
    </div>
  );
}
